/*
 * Copyright (c) 2022, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {
	ShopperContexts,
	ShopperSearch,
	ShopperPromotions,
	ShopperProducts,
	ShopperLogin,
	ShopperGiftCertificates,
	ShopperCustomers,
} from 'commerce-sdk-isomorphic';
import ShopperBaskets from './shopper-baskets';
import {getAppOrigin} from 'coreUtils/url';
import OcapiShopperOrders from './ocapi-shopper-orders';
import {isError, isTokenValid} from './utils';
import Auth from './auth';
import EinsteinAPI from './einstein';
import ManagerRepository from 'coreClasses/ManagerRepository';
import {isServer, timeout} from 'coreUtils/utils';
import {
	CustomShopperAddress,
	CustomShopperApplepay,
	CustomShopperAppointments,
	CustomShopperContext,
	CustomShopperCustomer,
	CustomShopperGooglepay,
	CustomShopperLoyalty,
	CustomShopperNewsletter,
	CustomShopperNotifyMe,
	CustomShopperOrders,
	CustomShopperPersonalShopper,
	CustomShopperQuickSearch,
	CustomShopperReservations,
	CustomShopperReturnAuthorizations,
	CustomShopperProduct,
	CustomShopperStores,
	CustomShopperSystem,
	CustomShopperWishlist,
	CustomShopperWorldpay,
	CustomShopperAbandonedCart,
	CustomShopperRefinementImages
} from './custom-apis';

/**
 * The configuration details for the connecting to the API.
 * @typedef {Object} ClientConfig
 * @property {string} [proxy] - URL to proxy fetch calls through.
 * @property {string} [headers] - Request headers to be added to requests.
 * @property {Object} [parameters] - API connection parameters for SDK.
 * @property {string} [parameters.clientId]
 * @property {string} [parameters.organizationId]
 * @property {string} [parameters.shortCode]
 * @property {string} [parameters.siteId]
 * @property {string} [parameters.version]
 */

/**
 * An object containing the customer's login credentials.
 * @typedef {Object} CustomerCredentials
 * @property {string} credentials.email
 * @property {string} credentials.password
 */

/**
 * Salesforce Customer object.
 * {@link https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shoppercustomers.html#customer}}
 * @typedef {Object} Customer
 */

/**
 * A wrapper class that proxies calls to the underlying commerce-sdk-isomorphic.
 * The sdk class instances are created automatically with the given config.
 */
class CommerceAPI {
	private _auth: Record<string, Auth> = {};
	private readonly _config: any;
	private _sdkInstances: any;
	public einstein: EinsteinAPI;
	public managerRepository: ManagerRepository;
	public shopperCustomers: ShopperCustomers<any>;
	public shopperCustomersCustomProxy: ShopperCustomers<any>;
	public shopperBaskets: ShopperBaskets;
	public shopperGiftCertificates: ShopperGiftCertificates<any>;
	public shopperLogin: ShopperLogin<any>;
	public shopperOrders: OcapiShopperOrders;
	public shopperProducts: ShopperProducts<any>;
	public shopperPromotions: ShopperPromotions<any>;
	public shopperSearch: ShopperSearch<any>;
	public shopperContexts: ShopperContexts<any>;
	public customShopperAddress: CustomShopperAddress;
	public customShopperApplepay: CustomShopperApplepay;
	public customShopperAppointments: CustomShopperAppointments;
	public customShopperContext: CustomShopperContext;
	public customShopperCustomer: CustomShopperCustomer;
	public customShopperGooglepay: CustomShopperGooglepay;
	public customShopperLoyalty: CustomShopperLoyalty;
	public customShopperNewsletter: CustomShopperNewsletter;
	public customShopperNotifyMe: CustomShopperNotifyMe;
	public customShopperOrders: CustomShopperOrders;
	public customShopperPersonalShopper: CustomShopperPersonalShopper;
	public customShopperQuickSearch: CustomShopperQuickSearch;
	public customShopperReservations: CustomShopperReservations;
	public customShopperReturnAuthorizations: CustomShopperReturnAuthorizations;
	public customShopperProduct: CustomShopperProduct;
	public customShopperStores: CustomShopperStores;
	public customShopperSystem: CustomShopperSystem;
	public customShopperWishlist: CustomShopperWishlist;
	public customShopperWorldpay: CustomShopperWorldpay;
	public customShopperAbandonedCart: CustomShopperAbandonedCart;
	public customShopperRefinementImages: CustomShopperRefinementImages;

	/**
	 * Create an instance of the API with the given config.
	 * @param {ClientConfig} config - The config used to instantiate SDK apis.
	 * @param {ManagerRepository} managerRepository - ManagerRepository instance
	 */
	constructor(
		config: { proxyPath?: string, overrideProxyPath?: string | null } = {},
		managerRepository: ManagerRepository
	) {
		const {proxyPath, overrideProxyPath = null, ...restConfig} = config;
		// Client-side requests should be proxied via the configured path.
		const proxy: string = `${getAppOrigin()}${proxyPath}`;

		this._config = {proxy, proxyPath, ...restConfig};
		this.managerRepository = managerRepository;

		if (this._config.einsteinConfig?.einsteinId) {
			this.einstein = new EinsteinAPI(this);
		}

		// A mapping of property names to the SDK class constructors we'll be
		// providing instances for.
		//
		// NOTE: `sendLocale` and `sendCurrency` for sending locale and currency info to the API:
		// - boolean, if you want to affect _all_ methods for a given API
		// - OR an array (listing the API's methods), if you want to affect only certain methods of an API
		const apiConfigs = {
			shopperCustomers: {
				api: ShopperCustomers,
				sendLocale: false,
				sendSiteId: true,
				contextAwareness: false
			},
			shopperCustomersCustomProxy: {
				api: ShopperCustomers,
				sendLocale: false,
				sendSiteId: true,
				contextAwareness: false,
				localOptions: {
					proxy: `${getAppOrigin()}/mobify/custom-scapi`
				}
			},
			shopperBaskets: {
				api: ShopperBaskets,
				sendLocale: ['getPaymentMethodsForBasket'],
				sendCurrency: ['createBasket'],
				sendSiteId: true,
				contextAwareness: true,
				apiVersion: 'v2',
			},
			shopperGiftCertificates: {
				api: ShopperGiftCertificates,
				sendSiteId: true
			},
			shopperLogin: {
				api: ShopperLogin,
				sendLocale: false,
				localOptions: this._config.useSLASPrivateClient
					? {
						proxy: `${getAppOrigin()}/mobify/slas/private`
					}
					: {}
			},
			shopperOrders: {
				api: OcapiShopperOrders,
				contextAwareness: false
			},
			shopperProducts: {
				api: ShopperProducts,
				sendCurrency: ['getProduct', 'getProducts'],
				sendSiteId: ['getProduct', 'getProducts', 'getCategory'],
				contextAwareness: true
			},
			shopperPromotions: {
				api: ShopperPromotions
			},
			shopperSearch: {
				api: ShopperSearch,
				sendCurrency: ['productSearch', 'getSearchSuggestions'],
				sendSiteId: ['productSearch', 'getSearchSuggestions'],
				contextAwareness: true
			},
			shopperContexts: {
				api: ShopperContexts,
				sendLocale: false,
				sendSiteId: [
					'getShopperContext',
					'createShopperContext',
					'updateShopperContext',
					'deleteShopperContext'
				],
				predefinedParamsFunc: () => {
					return {usid: this.auth.usid};
				}
			},
			customShopperAddress: {
				api: CustomShopperAddress,
				sendLocale: false,
				customApiConfig: {
					apiName: 'address-api',
					apiVersion: 'v1'
				}
			},
			customShopperApplepay: {
				api: CustomShopperApplepay,
				sendLocale: true,
				stringifyBody: true,
				customApiConfig: {
					apiName: 'applepay-api',
					apiVersion: 'v1'
				}
			},
			customShopperAppointments: {
				api: CustomShopperAppointments,
				sendLocale: true,
				stringifyBody: true,
				customApiConfig: {
					apiName: 'appointments-api',
					apiVersion: 'v1'
				}
			},
			customShopperCustomer: {
				api: CustomShopperCustomer,
				sendLocale: false,
				stringifyBody: true,
				customApiConfig: {
					apiName: 'customer-api',
					apiVersion: 'v1'
				}
			},
			customShopperGooglepay: {
				api: CustomShopperGooglepay,
				sendLocale: false,
				customApiConfig: {
					apiName: 'googlepay-api',
					apiVersion: 'v1'
				}
			},
			customShopperLoyalty: {
				api: CustomShopperLoyalty,
				sendLocale: false,
				stringifyBody: ['joinToLoyalty'],
				customApiConfig: {
					apiName: 'loyalty-api',
					apiVersion: 'v1'
				}
			},
			customShopperNewsletter: {
				api: CustomShopperNewsletter,
				sendLocale: false,
				stringifyBody: [
					'searchNewsletterProfile',
					'createNewsletterProfile',
					'updateNewsletterProfile',
					'subscribeToNewsletter',
					'addNewsletterPreferences',
				],
				customApiConfig: {
					apiName: 'newsletter-api',
					apiVersion: 'v1'
				},
				localOptions: {
					proxy: `${getAppOrigin()}/mobify/custom-scapi`
				}
			},
			customShopperNotifyMe: {
				api: CustomShopperNotifyMe,
				sendLocale: true,
				stringifyBody: true,
				customApiConfig: {
					apiName: 'notifyme-api',
					apiVersion: 'v1'
				}
			},
			customShopperOrders: {
				api: CustomShopperOrders,
				sendLocale: false,
				customApiConfig: {
					apiName: 'orders-api',
					apiVersion: 'v1'
				}
			},
			customShopperPersonalShopper: {
				api: CustomShopperPersonalShopper,
				sendLocale: true,
				stringifyBody: ['generateUrl'],
				customApiConfig: {
					apiName: 'personalshopper-api',
					apiVersion: 'v1'
				}
			},
			customShopperQuickSearch: {
				api: CustomShopperQuickSearch,
				sendLocale: true,
				customApiConfig: {
					apiName: 'quicksearch-api',
					apiVersion: 'v1'
				}
			},
			customShopperReservations: {
				api: CustomShopperReservations,
				sendLocale: false,
				stringifyBody: ['createReservation'],
				customApiConfig: {
					apiName: 'reservations-api',
					apiVersion: 'v1'
				}
			},
			customShopperReturnAuthorizations: {
				api: CustomShopperReturnAuthorizations,
				sendLocale: ['getReturnAuthorizationReasons'],
				sendCustomCurrency: ['createReturnAuthorization'],
				stringifyBody: ['createReturnAuthorization', 'updateReturnAuthorization', 'preValidateReturnAuthorization', 'getReturnLink'],
				customApiConfig: {
					apiName: 'returnauthorizations-api',
					apiVersion: 'v1'
				}
			},
			customShopperProduct: {
				api: CustomShopperProduct,
				sendLocale: false,
				stringifyBody: true,
				sendCustomCurrency: ['calculateCustomProductPrice'],
				customApiConfig: {
					apiName: 'product-api',
					apiVersion: 'v1'
				}
			},
			customShopperStores: {
				api: CustomShopperStores,
				sendLocale: ['getStore', 'findNearestStore'],
				customApiConfig: {
					apiName: 'stores-api',
					apiVersion: 'v1'
				},
				localOptions: {
					proxy: `${getAppOrigin()}/mobify/custom-scapi`
				}
			},
			customShopperSystem: {
				api: CustomShopperSystem,
				sendLocale: false,
				stringifyBody: true,
				customApiConfig: {
					apiName: 'system-api',
					apiVersion: 'v1'
				}
			},
			customShopperWishlist: {
				api: CustomShopperWishlist,
				sendLocale: false,
				stringifyBody: true,
				customApiConfig: {
					apiName: 'wishlist-api',
					apiVersion: 'v1'
				}
			},
			customShopperWorldpay: {
				api: CustomShopperWorldpay,
				sendLocale: false,
				customApiConfig: {
					apiName: 'worldpay-api',
					apiVersion: 'v1'
				}
			},
			customShopperContext: {
				api: CustomShopperContext,
				sendLocale: true,
				stringifyBody: true,
				customApiConfig: {
					apiName: 'context-api',
					apiVersion: 'v1'
				}
			},
			customShopperAbandonedCart: {
				api: CustomShopperAbandonedCart,
				sendLocale: true,
				stringifyBody: true,
				customApiConfig: {
					apiName: 'basket-api',
					apiVersion: 'v1'
				}
			},
			customShopperRefinementImages: {
				api: CustomShopperRefinementImages,
				sendLocale: false,
				customApiConfig: {
					apiName: 'refinement-images-api',
					apiVersion: 'v1'
				}
			}
		};
		// Instantiate the SDK class proxies and create getters from our api mapping.
		// The proxy handlers are called when accessing any of the mapped SDK class
		// proxies, executing various pre-defined hooks for tapping into or modifying
		// the outgoing method parameters and/or incoming SDK responses
		const self = this;
		const isExternalApp = self.managerRepository.getUiMgr().isCORSRequest();
		const isOverrideProxyPath = isExternalApp && overrideProxyPath;

		Object.keys(apiConfigs).forEach((key) => {
			const SdkClass = apiConfigs[key].api;
			const options: Record<string, any> = {
				...this._config,
				...apiConfigs[key].localOptions || {},
				customApiConfig: apiConfigs[key].customApiConfig
			};
			options.parameters.version = apiConfigs[key].apiVersion;

			self._sdkInstances = {
				...self._sdkInstances,
				[key]: new Proxy(new SdkClass(options), {
					get: function (obj, prop) {
						// External App case: mobify prefix should be removed
						if (isOverrideProxyPath && obj.clientConfig && obj.clientConfig.proxy) {
							obj.clientConfig.proxy = obj.clientConfig.proxy.replace(
								proxyPath,
								overrideProxyPath
							);
						}

						if (typeof obj[prop] === 'function') {
							return (...args) => {
								const fetchOptions = args[0];
								let {locale, currency} = self._config;

								if (!isServer) {
									/**
									 * Currency and locale may be changed in another tab
									 * this causes missing prices, because the context currency is
									 * different from the requested one.
									 */
									if (localStorage.getItem('scapi:currency')) {
										currency = localStorage.getItem('scapi:currency');
									}

									if (localStorage.getItem('scapi:locale')) {
										locale = localStorage.getItem('scapi:locale');
									}
								}

								const {siteId} = self._config.parameters;

								// External App case: mobify prefix should be removed
								// TODO: Should be optimized in the future to remove hardcoded param (shopperBasketsClient)
								if (
									isOverrideProxyPath
									&& 'shopperBasketsClient' in obj
									&& obj.shopperBasketsClient.clientConfig.proxy
								) {
									obj.shopperBasketsClient.clientConfig.proxy =
										obj.shopperBasketsClient.clientConfig.proxy.replace(
											proxyPath,
											overrideProxyPath
										);
								}

								if (fetchOptions.ignoreHooks) {
									return obj[prop](...args);
								}

								// Inject the locale and currency to the API call via its parameters.
								//
								// NOTE: The commerce sdk isomorphic will complain if you pass parameters to
								// it that it doesn't expect, this is why we only add the locale and currency
								// to some of the API calls.

								// By default we send the locale param and don't send the currency param.
								const {
									sendLocale = true,
									sendCurrency = false,
									sendCustomCurrency = false,
									predefinedParamsFunc = null,
									useCachedApi = false,
									sendSiteId = false,
									contextAwareness = false,
									stringifyBody = false
								} = apiConfigs[key];

								if (useCachedApi) {
									obj.clientConfig.proxy = obj.clientConfig.proxy.replace('proxy', 'caching');
								}

								const includeGlobalLocale: boolean = Array.isArray(sendLocale)
									? sendLocale.includes(prop)
									: !!sendLocale;
								const includeGlobalCurrency: boolean = Array.isArray(sendCurrency)
									? sendCurrency.includes(prop)
									: !!sendCurrency;
								const includeCustomCurrency: boolean = Array.isArray(sendCustomCurrency)
									? sendCustomCurrency.includes(prop)
									: !!sendCustomCurrency;
								const includeGlobalSiteId: boolean = Array.isArray(sendSiteId)
									? sendSiteId.includes(prop)
									: !!sendSiteId;
								const shouldStringifyBody: boolean = Array.isArray(stringifyBody)
									? stringifyBody.includes(prop)
									: !!stringifyBody;

								fetchOptions.parameters = {
									...(includeGlobalLocale ? {locale} : {}),
									...(includeGlobalCurrency ? {currency} : {}),
									...(includeCustomCurrency ? {c_currency: currency} : {}),
									...(includeGlobalSiteId ? {siteId} : {}),
									// Allowing individual API calls to override the global locale/currency
									...fetchOptions.parameters,
								};

								if (
									shouldStringifyBody
									&& fetchOptions.body
									&& Object.keys(fetchOptions.body).length > 0
								) {
									fetchOptions.body = JSON.stringify(fetchOptions.body);
								}

								return self.willSendRequest(prop, contextAwareness, ...args).then((newArgs) => {
									if (typeof predefinedParamsFunc === 'function') {
										newArgs[0].parameters = {
											...predefinedParamsFunc.call(self),
											...newArgs[0].parameters
										};
									}

									return obj[prop](...newArgs).then((res) => {
										return (self.didReceiveResponse as any)(res, newArgs);
									});
								});
							};
						}

						return obj[prop];
					}
				})
			};

			Object.defineProperty(self, key, {
				get() {
					return self._sdkInstances[key];
				}
			});
		});

		this.getConfig = this.getConfig.bind(this);
	}

	/**
	 * Returns the Auth instance
	 */
	get auth() {
		if (!this._auth[this._config.parameters.siteId]) {
			this._auth[this._config.parameters.siteId] = new Auth(
				this,
				this.managerRepository,
				this._config.parameters.siteId,
				this._config.parameters.organizationId
			);
		}

		return this._auth[this._config.parameters.siteId];
	}

	/**
	 * Returns the api client configuration
	 * @returns {ClientConfig}
	 */
	getConfig() {
		return this._config;
	}

	/**
	 * Updates the locale in api client configuration
	 * @param {string} locale string
	 */
	setLocale(locale) {
		this._config.locale = locale;
	}

	/**
	 * Updates the currency in api client configuration
	 * @param {string} currency currency
	 */
	setCurrency(currency) {
		this._config.currency = currency;
	}

	/**
	 * Updates the siteId in api client configuration
	 * @param {string} siteId siteId
	 */
	setSiteId(siteId) {
		if (siteId) {
			this.shopperLogin.clientConfig.parameters.siteId = siteId;

			if (this._config.parameters.siteId !== siteId) {
				this.auth.deleteAuthToken();
				this._config.parameters.siteId = siteId;
			}
		}
	}

	/**
	 * Retrieves the Einstein API object.
	 * @returns {EinsteinAPI} The Einstein API object.
	 */
	getEinsteinAPI() {
		return this.einstein;
	}

	/**
	 * Executed before every proxied method call to the SDK. Provides the method
	 * name and arguments. This can be overidden in a subclass to perform any
	 * logging or modifications to arguments before the request is sent.
	 * @param {string} methodName - The name of the sdk method that will be called.
	 * @param {...*} args - Original arguments for the SDK method.
	 * @returns {Promise<Array>} - Updated arguments that will be passed to the SDK method
	 */
	async willSendRequest(methodName, contextAwareness, ...params) {
		// We never need to modify auth request headers for these methods

		if (
			methodName === 'authenticateCustomer' ||
			methodName === 'authorizeCustomer' ||
			methodName === 'getAccessToken'
		) {
			return params;
		}

		let customerAwaitRetries = 0;

		while (
			methodName !== 'getCustomer' &&
			!this.managerRepository.getCustomerMgr().getCurrentCustomer().isInitialized &&
			customerAwaitRetries < 3
		) {
			customerAwaitRetries++;
			await timeout(500);
		}

		// If the token is invalid (missing, past/nearing expiration), we issue
		//  a login call, which will attempt to refresh the token or get a new
		//  guest token. Once login is complete, we can proceed.
		if (
			!isTokenValid(this.auth.authToken) ||
			(methodName !== 'getCustomer' &&
				!this.managerRepository.getCustomerMgr().getCurrentCustomer().isInitialized)
		) {
			// NOTE: Login will update `this.auth.authToken` with a fresh token
			await this.managerRepository.getCustomerMgr().loginCustomer();
		}

		if (contextAwareness && !this.managerRepository.getUiMgr().isLivePreview()) {
			await this.managerRepository.getCustomerMgr().validateCustomerContext();
		}

		// Apply the appropriate auth headers and return new options
		const [fetchOptions, ...restParams] = params;
		const newFetchOptions = {
			...fetchOptions,
			headers: {
				...fetchOptions.headers,
				'Authorization': this.auth.authToken
			}
		};

		return [newFetchOptions, ...restParams];
	}

	/**
	 * Executed when receiving a response from an SDK request. The response data
	 * can be mutated or inspected before being passed back to the caller. Should
	 * be overidden in a subclass.
	 * @param {*} response - The response from the SDK method call.
	 * @returns {*} - The response to be passed back to original caller.
	 */
	didReceiveResponse(response) {
		if (isError(response)) {
			return {...response, isError: true, message: response.detail};
		}

		return response;
	}
}

export default CommerceAPI;
